import React, { useState, useEffect, useCallback, useRef } from 'react';

/* ========== タイムピッカー (ドラムロール風) ========== */
const TimePicker = ({ value, onChange }) => {
  const [show, setShow] = useState(false);
  const [hour, setHour] = useState(value ? parseInt(value.split(':')[0]) : 12);
  const [minute, setMinute] = useState(value ? parseInt(value.split(':')[1]) : 0);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShow(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const pad = (n) => String(n).padStart(2, '0');

  const handleConfirm = () => {
    onChange(`${pad(hour)}:${pad(minute)}`);
    setShow(false);
  };

  const scrollHandler = (setter, max, current) => (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 1 : -1;
    setter((current + delta + max) % max);
  };

  const arrowStyle = {
    background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer',
    padding: '4px 12px', color: '#37474f'
  };
  const drumStyle = {
    fontSize: '28px', fontWeight: 'bold', width: '60px', textAlign: 'center',
    padding: '8px 0', background: '#f5f5f5', borderRadius: '6px', userSelect: 'none'
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
      <input
        type="text"
        value={value || ''}
        placeholder="時間を選択"
        readOnly
        onClick={() => setShow(!show)}
        style={{ cursor: 'pointer', width: '100%', padding: '12px 16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px' }}
      />
      {show && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 1000,
          background: 'white', border: '1px solid #ddd', borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)', padding: '16px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
          minWidth: '200px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <button type="button" onClick={() => setHour((hour - 1 + 24) % 24)} style={arrowStyle}>&#9650;</button>
              <div onWheel={scrollHandler(setHour, 24, hour)} style={drumStyle}>{pad(hour)}</div>
              <button type="button" onClick={() => setHour((hour + 1) % 24)} style={arrowStyle}>&#9660;</button>
            </div>
            <span style={{ fontSize: '24px', fontWeight: 'bold' }}>:</span>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <button type="button" onClick={() => setMinute((minute - 5 + 60) % 60)} style={arrowStyle}>&#9650;</button>
              <div onWheel={scrollHandler(setMinute, 60, minute)} style={drumStyle}>{pad(minute)}</div>
              <button type="button" onClick={() => setMinute((minute + 5) % 60)} style={arrowStyle}>&#9660;</button>
            </div>
          </div>
          <button type="button" onClick={handleConfirm}
            style={{ background: '#37474f', color: 'white', border: 'none', padding: '8px 24px', borderRadius: '6px', cursor: 'pointer' }}>
            決定
          </button>
        </div>
      )}
    </div>
  );
};

const RELATIONSHIPS = [
  '本人', '配偶者', '父', '母', '息子', '娘',
  '祖父', '祖母', '孫息子', '孫娘',
  '兄', '姉', '弟', '妹',
  '義父', '義母', '義兄', '義姉', '義弟', '義妹',
  '義祖父', '義祖母',
];

// 「従業員から見た死亡者の続柄」→「死亡者から見た喪主(=従業員)の続柄」の対応（性別不明のため代表値を入れ、利用者が修正可）
const CHIEF_RELATIONSHIP_REVERSE = {
  '父': '長男',
  '母': '長男',
  '配偶者': '夫',
  '息子': '父',
  '娘': '父',
  '兄': '弟',
  '姉': '弟',
  '弟': '兄',
  '妹': '兄',
  '義父': '娘婿',
  '義母': '娘婿',
};


const CondolenceSubmit = () => {
  const [stores, setStores] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [invitation, setInvitation] = useState(null);
  const [tokenError, setTokenError] = useState('');
  const [tokenChecking, setTokenChecking] = useState(false);

  const [form, setForm] = useState({
    store_code: '',
    store_name: '',
    submitter_name: '',
    employee_category: '',
    deceased_name: '',
    deceased_name_kana: '',
    deceased_age: '',
    death_date: '',
    relationship: '',
    living_together: '',
    remarks: '',
    send_obituary: '',
    allow_attendance: '',
    request_telegram: '希望する',
    request_flowers: '希望する',
    chief_mourner_name: '',
    chief_mourner_name_kana: '',
    chief_mourner_relationship: '',
    deceased_is_employee: '',
    wake_date: '',
    wake_time: '18:00',
    wake_time_undecided: false,
    funeral_date: '',
    funeral_time: '',
    funeral_time_undecided: false,
    venue_name: '',
    venue_postal_code: '',
    venue_address: '',
    venue_phone: '',
  });

  useEffect(() => {
    fetch('/api/condolence/stores')
      .then(res => res.json())
      .then(data => setStores(data.stores || []))
      .catch(err => console.error(err));
  }, []);

  // URLクエリの token を検証して招待情報を自動反映
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) return;
    setTokenChecking(true);
    fetch(`/api/condolence/invitations/${encodeURIComponent(token)}`)
      .then(async res => {
        const data = await res.json();
        if (!res.ok) {
          setTokenError(data.error || 'URLが無効です');
          return;
        }
        setInvitation({ ...data, token });
        setForm(f => ({
          ...f,
          store_code: data.store_code || '',
          submitter_name: data.employee_name || '',
          employee_category: data.category || '',
          relationship: data.relationship || '',
        }));
      })
      .catch(() => setTokenError('URLの検証に失敗しました'))
      .finally(() => setTokenChecking(false));
  }, []);

  // 店舗一覧読み込み後に store_name を補完（token経由で store_code 先行設定済の場合）
  useEffect(() => {
    if (invitation && stores.length > 0 && !form.store_name) {
      const s = stores.find(s => s.store_code === invitation.store_code);
      if (s) setForm(f => ({ ...f, store_name: s.store_name }));
    }
  }, [stores, invitation, form.store_name]);

  const setField = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
  };

  const handleStoreChange = (e) => {
    const code = e.target.value;
    const store = stores.find(s => s.store_code === code);
    setForm(f => ({
      ...f,
      store_code: code,
      store_name: store ? store.store_name : '',
    }));
  };

  const handlePostalCodeChange = useCallback(async (value) => {
    setForm(f => ({ ...f, venue_postal_code: value }));
    const digits = (value || '').replace(/[^0-9]/g, '');
    if (digits.length === 7) {
      try {
        const res = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${digits}`);
        if (res.ok) {
          const data = await res.json();
          if (data.results && data.results[0]) {
            const r = data.results[0];
            const address = `${r.address1 || ''}${r.address2 || ''}${r.address3 || ''}`;
            setForm(f => ({ ...f, venue_address: address }));
          }
        }
      } catch {}
    }
  }, []);

  const handleNameKana = useCallback(async (nameField, kanaField, value) => {
    setForm(f => ({ ...f, [nameField]: value }));
    if (value.trim()) {
      try {
        const res = await fetch('/api/condolence/public/convert-kana', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: value }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.kana) {
            setForm(f => ({ ...f, [kanaField]: data.kana }));
          }
        }
      } catch {}
    }
  }, []);

  // 訃報・弔電・供花が「全て辞退」のときだけ葬儀情報を隠す（基本は表示）
  const needsAdditionalInfo = !(
    form.send_obituary === '辞退する' &&
    form.request_telegram === '辞退' &&
    form.request_flowers === '辞退'
  );

  const handleSubmit = async () => {
    if (!form.store_code) { setError('店舗を選択してください'); return; }
    if (!form.submitter_name) { setError('氏名を入力してください'); return; }
    if (!form.employee_category) { setError('区分を選択してください'); return; }
    if (!form.deceased_name) { setError('死亡者の氏名を入力してください'); return; }
    if (!form.relationship) { setError('続柄を選択してください'); return; }
    if (!form.send_obituary) { setError('訃報の送付について選択してください'); return; }

    if (needsAdditionalInfo) {
      if (!form.chief_mourner_name) { setError('喪主の氏名を入力してください'); return; }
    }

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        ...form,
        wake_time: form.wake_time_undecided ? '未定' : form.wake_time,
        funeral_time: form.funeral_time_undecided ? '未定' : form.funeral_time,
        review_base_url: window.location.origin,
        token: invitation?.token,
      };
      const res = await fetch('/api/condolence/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSuccess(true);
      } else {
        const data = await res.json();
        setError(data.error || '提出に失敗しました');
      }
    } catch {
      setError('通信エラーが発生しました');
    } finally {
      setSubmitting(false);
    }
  };

  if (tokenChecking) {
    return (
      <div className="form-page">
        <header className="form-header" style={{ background: '#37474f' }}>
          <h1 style={{ marginLeft: '16px' }}>弔意情報提出</h1>
        </header>
        <main className="form-main">
          <div className="form-card" style={{ textAlign: 'center', padding: '48px 32px', color: '#666' }}>
            URLを確認しています...
          </div>
        </main>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="form-page">
        <header className="form-header" style={{ background: '#37474f' }}>
          <h1 style={{ marginLeft: '16px' }}>弔意情報提出</h1>
        </header>
        <main className="form-main">
          <div className="form-card" style={{ textAlign: 'center', padding: '48px 32px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', color: '#d32f2f' }}>&#9888;</div>
            <h2 style={{ color: '#d32f2f', marginBottom: '16px' }}>URLが無効です</h2>
            <p style={{ color: '#666', lineHeight: '1.8' }}>
              {tokenError}<br />
              お手数ですが、店舗担当者へご連絡ください。
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (success) {
    return (
      <div className="form-page">
        <header className="form-header" style={{ background: '#37474f' }}>
          <h1 style={{ marginLeft: '16px' }}>弔意情報提出</h1>
        </header>
        <main className="form-main">
          <div className="form-card" style={{ textAlign: 'center', padding: '48px 32px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10004;</div>
            <h2 style={{ color: '#2e7d32', marginBottom: '16px' }}>提出完了しました</h2>
            <p style={{ color: '#666', lineHeight: '1.8', marginBottom: '32px' }}>
              閉じるボタンを押してください。
            </p>
            <button
              onClick={() => { window.close(); }}
              style={{
                background: '#37474f', color: 'white', border: 'none',
                padding: '14px 48px', fontSize: '16px', borderRadius: '6px',
                cursor: 'pointer'
              }}>
              閉じる
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="form-page">
      <header className="form-header" style={{ background: '#37474f' }}>
        <h1 style={{ marginLeft: '16px' }}>弔意情報提出</h1>
      </header>

      <main className="form-main">
        <div className="section-bar" style={{ background: '#eceff1' }}>
          <span className="icon">&#128220;</span>
          <span className="title" style={{ color: '#37474f' }}>弔意に関する情報を入力してください</span>
        </div>

        {invitation && (
          <div style={{
            background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '6px',
            padding: '12px 16px', margin: '12px 0', color: '#1565c0', fontSize: '14px',
          }}>
            {invitation.employee_name} 様 宛の入力リンクです。
            店舗・氏名・区分・続柄は自動入力済みです（変更できません）。
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="form-card">
          {/* === 店舗・提出者情報 === */}
          <h3 style={sectionTitle}>店舗・提出者情報</h3>

          <div className="date-time-row">
            <div className="form-group">
              <label>店舗<span className="required">*</span></label>
              <select value={form.store_code} onChange={handleStoreChange} disabled={!!invitation}>
                <option value="">選択してください</option>
                {stores.map(s => (
                  <option key={s.store_code} value={s.store_code}>
                    {s.store_code} - {s.store_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>提出日</label>
              <input type="text" value={new Date().toLocaleDateString('ja-JP')} disabled />
            </div>
          </div>

          <div className="form-group">
            <label>氏名<span className="required">*</span></label>
            <input type="text" value={form.submitter_name}
              onChange={setField('submitter_name')} placeholder="あなたの氏名を入力"
              disabled={!!invitation} />
          </div>

          <div className="form-group">
            <label>区分<span className="required">*</span></label>
            <select value={form.employee_category} onChange={setField('employee_category')} disabled={!!invitation}>
              <option value="">選択してください</option>
              <option value="社員">社員</option>
              <option value="社員（出向）">社員（出向）</option>
              <option value="アルバイト">アルバイト</option>
            </select>
          </div>

          <hr className="form-divider" />

          {/* === 故人情報 === */}
          <h3 style={sectionTitle}>故人情報</h3>

          <div className="form-group">
            <label>死亡者の氏名<span className="required">*</span></label>
            <input type="text" value={form.deceased_name}
              onChange={(e) => handleNameKana('deceased_name', 'deceased_name_kana', e.target.value)}
              placeholder="故人の氏名を入力" />
          </div>

          <div className="form-group">
            <label>フリガナ <span style={{ fontSize: '12px', color: '#888', fontWeight: 'normal' }}>（自動入力・修正可）</span></label>
            <input type="text" value={form.deceased_name_kana}
              onChange={setField('deceased_name_kana')} placeholder="カタカナ" />
          </div>

          <div className="form-group">
            <label>享年</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="number" value={form.deceased_age}
                onChange={setField('deceased_age')} placeholder="例: 85" min="0" max="150"
                style={{ flex: 1 }} />
              <span>歳</span>
            </div>
          </div>

          <div className="form-group">
            <label>死亡年月日</label>
            <input type="date" value={form.death_date} onChange={setField('death_date')} />
          </div>

          <div className="form-group">
            <label>自分から見た続柄<span className="required">*</span></label>
            <select value={form.relationship} onChange={setField('relationship')} disabled={!!invitation}>
              <option value="">選択してください</option>
              {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>別居・同居</label>
            <div className="radio-group">
              {['同居', '別居'].map(v => (
                <label key={v} className="radio-option">
                  <input type="radio" name="living_together" value={v}
                    checked={form.living_together === v}
                    onChange={setField('living_together')} />
                  {v}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>喪主は当社従業員か</label>
            <div className="radio-group">
              {['従業員', '従業員以外'].map(v => (
                <label key={v} className="radio-option">
                  <input type="radio" name="deceased_is_employee" value={v}
                    checked={form.deceased_is_employee === v}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '従業員') {
                        const name = form.submitter_name || '';
                        const reverse = CHIEF_RELATIONSHIP_REVERSE[form.relationship] || '';
                        setForm(f => ({
                          ...f,
                          deceased_is_employee: val,
                          chief_mourner_name: name,
                          chief_mourner_relationship: reverse,
                        }));
                        // フリガナ自動取得
                        if (name.trim()) {
                          fetch('/api/condolence/public/convert-kana', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ text: name }),
                          })
                            .then(res => res.ok ? res.json() : null)
                            .then(data => {
                              if (data && data.kana) {
                                setForm(f => ({ ...f, chief_mourner_name_kana: data.kana }));
                              }
                            })
                            .catch(() => {});
                        }
                      } else {
                        setForm(f => ({ ...f, deceased_is_employee: val }));
                      }
                    }} />
                  {v}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>備考</label>
            <textarea
              value={form.remarks}
              onChange={setField('remarks')}
              placeholder="備考があれば入力してください"
              rows={3}
              style={{ width: '100%', padding: '12px 16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', resize: 'vertical' }}
            />
          </div>

          <hr className="form-divider" />

          {/* === 訃報・弔電・供花の希望 === */}
          <h3 style={sectionTitle}>訃報・弔電・供花について</h3>

          <div className="form-group">
            <label>社内に訃報を共有してもいいですか？<span className="required">*</span></label>
            <div className="radio-group">
              {['共有する', '辞退する'].map(v => (
                <label key={v} className="radio-option">
                  <input type="radio" name="send_obituary" value={v}
                    checked={form.send_obituary === v}
                    onChange={(e) => {
                      setForm(f => ({
                        ...f,
                        send_obituary: e.target.value,
                        allow_attendance: e.target.value === '辞退する' ? '' : f.allow_attendance,
                      }));
                    }} />
                  {v}
                </label>
              ))}
            </div>
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#d84315' }}>
              ※辞退した場合でも、自部署内/自店サポート室+共済会には共有されます。
            </div>
          </div>

          {form.send_obituary === '共有する' && (
            <div className="form-group">
              <div style={{ padding: '12px 16px', background: '#f5f5f5', borderRadius: '6px', marginBottom: '12px', fontSize: '15px', color: '#37474f' }}>
                規定に沿って会社関係者が参列します。
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '12px' }}>
                  <input
                    type="checkbox"
                    checked={form.allow_attendance === '辞退する'}
                    onChange={(e) => setForm(f => ({
                      ...f,
                      allow_attendance: e.target.checked ? '辞退する' : '望む',
                    }))}
                  />
                  ■遠慮する場合は☑をいれてください
                </label>
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="radio-option">
              <input
                type="checkbox"
                checked={form.request_telegram === '辞退'}
                onChange={(e) => setForm(f => ({
                  ...f,
                  request_telegram: e.target.checked ? '辞退' : '希望する',
                }))}
              />
              弔電を辞退する
            </label>
          </div>

          <div className="form-group">
            <label className="radio-option">
              <input
                type="checkbox"
                checked={form.request_flowers === '辞退'}
                onChange={(e) => setForm(f => ({
                  ...f,
                  request_flowers: e.target.checked ? '辞退' : '希望する',
                }))}
              />
              供花を辞退する
            </label>
          </div>

          {/* === 葬儀情報（訃報・弔電・供花を全て辞退の場合は非表示） === */}
          {needsAdditionalInfo && (
            <>
              <hr className="form-divider" />
              <h3 style={sectionTitle}>葬儀情報</h3>

              <div className="form-group">
                <label>喪主の氏名<span className="required">*</span></label>
                <input type="text" value={form.chief_mourner_name}
                  onChange={(e) => handleNameKana('chief_mourner_name', 'chief_mourner_name_kana', e.target.value)}
                  placeholder="喪主の氏名を入力" />
              </div>

              <div className="form-group">
                <label>喪主のフリガナ <span style={{ fontSize: '12px', color: '#888', fontWeight: 'normal' }}>（自動入力・修正可）</span></label>
                <input type="text" value={form.chief_mourner_name_kana}
                  onChange={setField('chief_mourner_name_kana')} placeholder="カタカナ" />
              </div>

              <div className="form-group">
                <label>死亡者から見た喪主の続柄</label>
                <select value={form.chief_mourner_relationship}
                  onChange={setField('chief_mourner_relationship')}>
                  <option value="">選択してください</option>
                  {['父', '母', '妻', '夫', '長男', '長女', '次男', '次女', '姉', '兄', '妹', '弟', '娘婿'].map(r =>
                    <option key={r} value={r}>{r}</option>
                  )}
                </select>
              </div>

              <div className="date-time-row">
                <div className="form-group">
                  <label>通夜の日程</label>
                  <input type="date" value={form.wake_date} onChange={setField('wake_date')} />
                </div>
                <div className="form-group">
                  <label>通夜の開始時間</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      {form.wake_time_undecided ? (
                        <input type="text" value="未定" disabled
                          style={{ width: '100%', padding: '12px 16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', background: '#f5f5f5', color: '#888' }} />
                      ) : (
                        <TimePicker value={form.wake_time}
                          onChange={(v) => setForm(f => ({ ...f, wake_time: v }))} />
                      )}
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: '#37474f', whiteSpace: 'nowrap' }}>
                      未定
                      <input type="checkbox"
                        checked={form.wake_time_undecided}
                        onChange={(e) => setForm(f => ({
                          ...f,
                          wake_time_undecided: e.target.checked,
                          wake_time: e.target.checked ? '' : (f.wake_time || '18:00'),
                        }))} />
                    </label>
                  </div>
                </div>
              </div>

              <div className="date-time-row">
                <div className="form-group">
                  <label>葬儀の日程</label>
                  <input type="date" value={form.funeral_date} onChange={setField('funeral_date')} />
                </div>
                <div className="form-group">
                  <label>葬儀の開始時間</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      {form.funeral_time_undecided ? (
                        <input type="text" value="未定" disabled
                          style={{ width: '100%', padding: '12px 16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', background: '#f5f5f5', color: '#888' }} />
                      ) : (
                        <TimePicker value={form.funeral_time}
                          onChange={(v) => setForm(f => ({ ...f, funeral_time: v }))} />
                      )}
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: '#37474f', whiteSpace: 'nowrap' }}>
                      未定
                      <input type="checkbox"
                        checked={form.funeral_time_undecided}
                        onChange={(e) => setForm(f => ({
                          ...f,
                          funeral_time_undecided: e.target.checked,
                          funeral_time: e.target.checked ? '' : f.funeral_time,
                        }))} />
                    </label>
                  </div>
                </div>
              </div>

              {(form.wake_time_undecided || form.funeral_time_undecided) && (
                <div style={{
                  background: '#fff8e1', border: '1px solid #ffb300', borderRadius: '6px',
                  padding: '10px 14px', marginBottom: '12px', color: '#bf360c', fontSize: '14px',
                }}>
                  通夜・葬儀の開始時間が判明しましたら速やかに店舗役職者または上長へお知らせください
                </div>
              )}

              <div className="form-group">
                <label>葬儀会場名</label>
                <input type="text" value={form.venue_name}
                  onChange={setField('venue_name')} placeholder="会場名を入力" />
              </div>

              <div className="date-time-row">
                <div className="form-group">
                  <label>会場郵便番号</label>
                  <input type="text" value={form.venue_postal_code}
                    onChange={(e) => handlePostalCodeChange(e.target.value)}
                    placeholder="0000000" />
                </div>
                <div className="form-group">
                  <label>会場住所</label>
                  <input type="text" value={form.venue_address}
                    onChange={setField('venue_address')} placeholder="住所を入力" />
                </div>
              </div>

              <div className="form-group">
                <label>会場電話番号</label>
                <input type="text" value={form.venue_phone}
                  onChange={setField('venue_phone')} placeholder="000-0000-0000" />
              </div>
            </>
          )}

          {/* === 提出ボタン === */}
          <div className="submit-section" style={{ marginTop: '32px' }}>
            <button className="submit-button" onClick={handleSubmit}
              disabled={submitting}
              style={{ background: '#37474f', padding: '16px 64px', fontSize: '18px' }}>
              {submitting ? '提出中...' : '提出する'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

const sectionTitle = {
  marginBottom: '16px',
  color: '#37474f',
  borderBottom: '2px solid #37474f',
  paddingBottom: '8px',
};

export default CondolenceSubmit;
